import type {EcommerceSalesChannel} from '@/types/salesChannels/index';
import {storeToRefs} from 'pinia';
import {computed} from 'vue';
import useBolEcommerceSalesChannels from './useBolEcommerceSalesChannels';
import useCscartEcommerceSalesChannels from './useCscartEcommerceSalesChannels';
import usePrestaEcommerceSalesChannels from './usePrestaEcommerceSalesChannels';
import useShopifyEcommerceSalesChannels from './useShopifyEcommerceSalesChannels';
import useWoocommerceEcommerceSalesChannels from './useWoocommerceEcommerceSalesChannels';
import useAccount from '@/composables/useAccount/useAccount';
import useLightspeedEcommerceSalesChannels from '@/composables/useSalesChannels/useLightspeedEcommerceSalesChannels.ts';
import useShop from '@/composables/useShop/useShop';
import {useSalesChannelStore} from '@/store/salesChannelStore/salesChannelStore';
import {isSalesChannelWithStatusDelegationIssued} from '@/views/SalesChannels/typeGuards';

type FetchSalesChannelByIdRequest = {
  ecommercePlatform: EcommerceSalesChannel['ecommercePlatform'];
  salesChannelId: EcommerceSalesChannel['id'];
};

export default function useSalesChannels() {
  const salesChannelStore = useSalesChannelStore();
  const {fetchBolSalesChannelById, getBolSalesChannels} = useBolEcommerceSalesChannels();
  const {fetchCscartSalesChannelById, getCscartSalesChannels} = useCscartEcommerceSalesChannels();
  const {fetchPrestaSalesChannelById, getPrestaSalesChannels} = usePrestaEcommerceSalesChannels();
  const {fetchShopifySalesChannelById, getShopifySalesChannels} = useShopifyEcommerceSalesChannels();
  const {fetchWoocommerceSalesChannelById, getWoocommerceSalesChannels} = useWoocommerceEcommerceSalesChannels();
  const {fetchLightspeedSalesChannelById, getLightspeedSalesChannels} = useLightspeedEcommerceSalesChannels();
  const {salesChannelList} = storeToRefs(salesChannelStore);
  const {shops} = useAccount();
  const {currentShop} = useShop();

  const salesChannelsForCurrentShop = computed<EcommerceSalesChannel[]>(() => {
    return salesChannelList.value.filter(
      (salesChannel: EcommerceSalesChannel) => salesChannel.shopId === currentShop.value.id.toString(),
    );
  });

  const salesChannelsManualImportForCurrentShop = computed(() => {
    return salesChannelsForCurrentShop.value.filter(
      (salesChannel) =>
        isSalesChannelWithStatusDelegationIssued(salesChannel) && !salesChannel.extraction.schedule.transformAndLoad,
    );
  });

  const fetchSalesChannels = async (shopIds: string[]) => {
    const channels = [
      {key: 'woocommerce', fn: getWoocommerceSalesChannels},
      {key: 'shopify', fn: getShopifySalesChannels},
      {key: 'bol', fn: getBolSalesChannels},
      {key: 'lightspeed', fn: getLightspeedSalesChannels},
      {key: 'presta', fn: getPrestaSalesChannels},
      {key: 'cscart', fn: getCscartSalesChannels},
    ];

    const settledResults = await Promise.allSettled(channels.map((channel) => channel.fn(shopIds)));
    const results: EcommerceSalesChannel[] = [];
    const errors: string[] = [];

    settledResults.forEach((result, index) => {
      const {key} = channels[index];

      if (result.status === 'fulfilled') {
        results.push(...result.value);
      } else {
        errors.push(key);
      }
    });

    return {errors};
  };

  const fetchSalesChannelByCurrentShopId = async () => {
    return fetchSalesChannels([currentShop.value.id.toString()]);
  };

  const fetchAllSalesChannels = async () => {
    const shopIds = shops.value.map((shop) => shop.id.toString());
    return fetchSalesChannels(shopIds);
  };

  const getSalesChannelById = (id: EcommerceSalesChannel['id']): EcommerceSalesChannel | undefined => {
    const salesChannelStore = useSalesChannelStore();
    return salesChannelStore.getSalesChannelById(id);
  };

  const fetchSalesChannelById = async ({
    ecommercePlatform,
    salesChannelId,
  }: FetchSalesChannelByIdRequest): Promise<EcommerceSalesChannel | undefined> => {
    let result;

    switch (ecommercePlatform) {
      case 'BOL':
        result = await fetchBolSalesChannelById(salesChannelId);
        break;
      case 'SHOPIFY':
        result = await fetchShopifySalesChannelById(salesChannelId);
        break;
      case 'WOOCOMMERCE':
        result = await fetchWoocommerceSalesChannelById(salesChannelId);
        break;
      case 'LIGHTSPEED':
        result = await fetchLightspeedSalesChannelById(salesChannelId);
        break;
      case 'PRESTA':
        result = await fetchPrestaSalesChannelById(salesChannelId);
        break;
      case 'CSCART':
        result = await fetchCscartSalesChannelById(salesChannelId);
        break;
    }

    return result;
  };

  const hasSalesChannelById = (id: EcommerceSalesChannel['id']): boolean => {
    const salesChannelStore = useSalesChannelStore();

    return salesChannelStore.hasSalesChannelById(id);
  };

  return {
    salesChannelList,
    salesChannelsForCurrentShop,
    salesChannelsManualImportForCurrentShop,
    fetchSalesChannels,
    fetchAllSalesChannels,
    fetchSalesChannelByCurrentShopId,
    fetchSalesChannelById,
    getSalesChannelById,
    hasSalesChannelById,
  };
}
